<template>
  <div class="menu-container">
    <v-toolbar class="custom-toolbar mb-4" flat dense>
      <img :src="logo" class="toolbar-icon" alt="Logo" />
      <v-toolbar-title class="custom-toolbar-title">
        {{ $t('menu.menu') }}
      </v-toolbar-title>
    </v-toolbar>

    <div class="avatar-container">
      <v-avatar v-if="profileLoaded" color="grey" size="150">
        <v-img :src="userProfile.profileImage" cover />
      </v-avatar>
      <v-progress-circular v-else :size="50" color="primary" indeterminate />
      <div v-if="profileLoaded" class="profile-info">
        <h2 class="avatar-name">
          {{ userProfile.name || userProfile.email }}
        </h2>
        <v-chip class="ma-2" color="black" variant="tonal">
          {{ userProfile.jobTitle }}
        </v-chip>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="400" transition="dialog-bottom-transition">
      <v-card>
        <v-card-title class="text-center">
          {{ dialogType === 'roomControl' ? 'Room Control Options' : 'Admin Panel Options' }}
        </v-card-title>
        <v-card-text>
          <v-row justify="space-around">
            <v-col v-for="control in dialogType === 'roomControl' ? roomControls : adminControls" :key="control.title"
              cols="auto" class="d-flex flex-column align-center">
              <v-card class="tile-card" max-width="120" :disabled="adminControls && control.disabled"
                @click="dialogType === 'roomControl' ? goToHardwareControl(control) : goToAdminControl(control)">
                <v-card-item class="d-flex flex-column align-center" style="padding: 16px">
                  <v-icon :style="{ color: control.color, fontSize: '36px' }">
                    {{ control.icon }}
                  </v-icon>
                  <span style="font-size: 0.85rem; font-weight: bold; margin-top: 0.8rem">
                    {{ control.title }}
                  </span>
                </v-card-item>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showLanguageDialog" max-width="300">
      <v-card>
        <v-card-title class="text-center">
          {{ $t('menu.language-selection') }}
        </v-card-title>
        <v-card-text>
          <v-row justify="space-around">
            <v-col cols="auto" class="d-flex flex-column align-center">
              <v-card class="tile-card" max-width="120" @click="setLanguage('en')"
                :class="{ selected: selectedLanguage === 'en' }" :disabled="selectedLanguage === 'en'">
                <v-card-item class="d-flex flex-column align-center" style="padding: 16px">
                  <v-img :src="usa_flag" class="flag-icon" />
                  <span style="font-size: 0.85rem; font-weight: bold; margin-top: 0.8rem">
                    {{ $t('menu.english') }}
                  </span>
                </v-card-item>
              </v-card>
            </v-col>
            <v-col cols="auto" class="d-flex flex-column align-center">
              <v-card class="tile-card" max-width="120" @click="setLanguage('de')"
                :class="{ selected: selectedLanguage === 'de' }" :disabled="selectedLanguage === 'de'">
                <v-card-item class="d-flex flex-column align-center" style="padding: 16px">
                  <v-img :src="german_flag" class="flag-icon" />
                  <span style="font-size: 0.85rem; font-weight: bold; margin-top: 0.8rem">
                    {{ $t('menu.german') }}
                  </span>
                </v-card-item>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="tonal" color="error" @click="showLanguageDialog = false">
            {{ $t('menu.cancel') }}
          </v-btn>
          <v-btn variant="tonal" color="success" @click="confirmLanguageChange">
            {{ $t('menu.change') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="cards-wrapper">
      <div class="cards-container">
        <div v-for="item in menuItems" :key="item.title" class="menu-card-col">
          <v-card class="menu-card" elevation="5" @click="goToMenuItem(item)" :disabled="item.disabled"
            :class="{ 'disabled-menu-card': item.disabled }">
            <!-- Premium Badge -->
            <v-chip v-if="item.isPremium" class="premium-badge" color="error" size="small" label>
              <v-icon start size="x-small">mdi-crown</v-icon>
              {{ $t('menu.premium') }}
            </v-chip>
            <v-card-item>
              <v-card-title>
                <div class="icon-wrapper" :style="{ background: item.background }">
                  <v-icon class="menu-icon" :style="{ color: item.color }">
                    {{ item.icon }}
                  </v-icon>
                </div>
              </v-card-title>
              <v-card-title class="card-title">
                {{ item.title }}
              </v-card-title>
              <v-card-subtitle class="card-subtitle">
                {{ item.subtitle }}
              </v-card-subtitle>
            </v-card-item>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, defineComponent, onMounted, watch } from 'vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { useRouter } from 'vue-router'
import { Device } from '@capacitor/device'
import { Capacitor } from '@capacitor/core'
import { isIos } from '@/services/OAuthService'
import { isWebPlatform, setCookie, getCookie } from '@/services/utils'
import { useOAuthService } from '@/services/OAuthService'
import { performLogout } from '@/controllers/BackboneAPI'
import Swal from 'sweetalert2'
import user_avatar_3d from '@/assets/General/user_avatar_3d.png'
import { getUserProfiles, getUserProfileById, getFile, getDataFromBlob, getFeatures, isPIA } from '@/controllers/BaseController'
import { storeBlob } from '@/services/utils'
import lodash from 'lodash'
import { useI18n } from 'vue-i18n'
import german_flag from '@/assets/General/german_flag.svg'
import usa_flag from '@/assets/General/usa_flag.svg'
import { Browser } from '@capacitor/browser'

export default defineComponent({
  name: 'MenuView',
  setup() {
    const { locale, t } = useI18n()
    const showLanguageDialog = ref(false)
    const selectedLanguage = ref('en')
    const logo = ref('')
    const dialog = ref(false)
    const dialogType = ref('')
    const isPia = ref(false)
    const isAdmin = ref(false)
    const profileLoaded = ref(false)
    const roomControls = ref([{ title: 'Room Control', icon: 'mdi-lightbulb-outline' }])
    const adminControls = ref([
      { title: 'Feedbacks Management', icon: 'mdi-message-outline', color: 'green', disabled: false },
      { title: 'Bookings Management', icon: 'mdi-calendar-check-outline', color: 'blue' },
      { title: 'Users Management', icon: 'mdi-account-supervisor', color: 'purple', disabled: false },
      { title: 'Incidents Management', icon: 'mdi-alert-circle-outline', color: 'red', disabled: false },
    ])

    const router = useRouter()
    const OAuthService = useOAuthService()
    const userProfile = ref(null)

    function isMobileDevice() {
      return Capacitor.isNativePlatform() && (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android')
    }

    const menuItems = ref([])

    const updateMenuItems = () => {
      console.log('Updating menu items, isAdmin:', isAdmin.value)

      menuItems.value = [
        {
          title: t('menu.profile.title'),
          subtitle: t('menu.profile.subtitle'),
          icon: 'mdi-account',
          color: 'white',
          background: '#9FC6A0',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.room_control.title'),
          subtitle: t('menu.room_control.subtitle'),
          icon: 'mdi-lightbulb-outline',
          color: 'white',
          background: '#F1C40F',
          disabled: !isAdmin.value,
          isPremium: true
        },
        {
          title: t('menu.locks_control.title'),
          subtitle: t('menu.locks_control.subtitle'),
          icon: 'mdi-lock-outline',
          color: 'white',
          background: '#F1C40F',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.elevator_control.title'),
          subtitle: t('menu.elevator_control.subtitle'),
          icon: 'mdi-elevator',
          color: 'white',
          background: '#8E44AD',
          disabled: !isAdmin.value,
          isPremium: true
        },
        {
          title: t('menu.analytics.title'),
          subtitle: t('menu.analytics.subtitle'),
          icon: 'mdi-chart-line',
          color: 'white',
          background: '#E67E22',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.occupancy.title'),
          subtitle: t('menu.occupancy.subtitle'),
          icon: 'mdi-map-marker-radius',
          color: 'white',
          background: '#27AE60',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.incidents.title'),
          subtitle: t('menu.incidents.subtitle'),
          icon: 'mdi-alert-circle-outline',
          color: 'white',
          background: '#E74C3C',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.language.title'),
          subtitle: t('menu.language.subtitle'),
          icon: 'mdi-translate',
          color: 'white',
          background: '#4A90E2',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.admin_panel.title'),
          subtitle: t('menu.admin_panel.subtitle'),
          icon: 'mdi-shield-account-outline',
          color: 'white',
          background: '#16A085',
          disabled: !isAdmin.value,
          isPremium: true
        },
        {
          title: t('menu.slido.title'),
          subtitle: t('menu.slido.subtitle'),
          icon: 'mdi-poll',
          color: 'white',
          background: '#2E86C1',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.commute.title'),
          subtitle: t('menu.commute.subtitle'),
          icon: 'mdi-train',
          color: 'white',
          background: '#34495E',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.vouchers.title'),
          subtitle: t('menu.vouchers.subtitle'),
          icon: 'mdi-ticket-percent-outline',
          color: 'white',
          background: '#3498DB',
          disabled: !isAdmin.value,
          isPremium: true
        },
        {
          title: t('menu.feedback.title'),
          subtitle: t('menu.feedback.subtitle'),
          icon: 'mdi-message-outline',
          color: 'white',
          background: '#F39C12',
          disabled: !isAdmin.value,
          isPremium: true
        },
        {
          title: t('menu.functions.title'),
          subtitle: t('menu.functions.subtitle'),
          icon: 'mdi-rocket',
          color: 'white',
          background: '#101F52',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.signups.title'),
          subtitle: t('menu.signups.subtitle'),
          icon: 'mdi-account-multiple-plus',
          color: 'white',
          background: '#9B59B6',
          disabled: false,
          isPremium: false
        },
        {
          title: t('menu.logout.title'),
          subtitle: t('menu.logout.subtitle'),
          icon: 'mdi-logout',
          color: 'white',
          background: '#9DA3A6',
          disabled: false,
          isPremium: false
        },
      ]
    }

    // Watch for isAdmin changes and update menu items
    watch(isAdmin, () => {
      console.log('isAdmin changed to:', isAdmin.value)
      updateMenuItems()
    })

    const goToMenuItem = async item => {
      // Prevent navigation if disabled
      if (item.disabled) {
        Swal.fire({
          title: t('menu.premium-title'),
          text: t('menu.premium-message'),
          icon: 'info',
          confirmButtonColor: '#4caf50',
          confirmButtonText: t('menu.premium-ok'),
        })
        return
      }

      switch (item.title) {
        case t('menu.profile.title'): router.push({ name: 'UserProfile' }).catch(err => console.error(err)); break
        case t('menu.room_control.title'): router.push({ name: 'RoomControlPois' }).catch(err => console.error(err)); break
        case t('menu.elevator_control.title'): router.push({ name: 'Elevator' }).catch(err => console.error(err)); break
        case t('menu.analytics.title'): router.push({ name: 'WorkSpaceDashboard' }).catch(err => console.error(err)); break
        case t('menu.occupancy.title'): router.push({ name: 'OccupancyView' }).catch(err => console.error(err)); break
        case t('menu.language.title'): showLanguageDialog.value = true; break
        case t('menu.admin_panel.title'): dialogType.value = 'adminPanel'; dialog.value = true; break
        case t('menu.feedback.title'): router.push({ name: 'FeedbackForm' }).catch(err => console.error(err)); break
        case t('menu.incidents.title'): router.push({ name: 'Incidents' }).catch(err => console.error(err)); break
        case t('menu.commute.title'): router.push({ name: 'CommuteDashboard' }).catch(err => console.error(err)); break
        case t('menu.vouchers.title'): router.push({ name: 'Vouchers' }).catch(err => console.error(err)); break
        case t('menu.slido.title'): router.push({ name: 'SlidoView' }).catch(err => console.error(err)); break
        case t('menu.functions.title'): router.push({ name: 'Functions' }).catch(err => console.error(err)); break
        case t('menu.signups.title'): router.push({ name: 'SignupsManagement' }).catch(err => console.error(err)); break
        case t('menu.logout.title'): await handleLogout(); break
        default: console.log('Unknown item clicked'); break
      }
    }

    async function handleLogout() {
      await performLogout()
    }

    const setLanguage = lang => selectedLanguage.value = lang

    const confirmLanguageChange = async () => {
      showLanguageDialog.value = false
      Swal.fire({
        title: t('language_change.title'),
        text: t('language_change.text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4caf50',
        cancelButtonColor: 'red',
        confirmButtonText: t('language_change.confirm'),
        cancelButtonText: t('language_change.cancel'),
      }).then(async result => {
        if (result.isConfirmed) {
          locale.value = selectedLanguage.value
          await setCookie('language', selectedLanguage.value, 365)
          updateMenuItems()
          window.location.reload()
        } else {
          showLanguageDialog.value = true
        }
      })
    }

    const cancelLanguageChange = () => {
      showLanguageDialog.value = false
    }

    const loadLanguageFromCookie = async () => {
      const savedLanguage = await getCookie('language')
      if (savedLanguage) {
        selectedLanguage.value = savedLanguage
        locale.value = savedLanguage
      } else {
        selectedLanguage.value = 'en'
        locale.value = 'en'
        await setCookie('language', 'en', 365)
      }
      updateMenuItems()
    }

    const openLanguageDialog = () => {
      selectedLanguage.value = locale.value
      showLanguageDialog.value = true
    }

    const goToHardwareControl = control => {
      dialog.value = false
      switch (control.title) {
        case 'Room Control': router.push({ name: 'RoomControlPois' }).catch(err => console.error(err)); break
        default: break
      }
    }

    const goToAdminControl = control => {
      dialog.value = false
      switch (control.title) {
        case 'Feedbacks Management': router.push({ name: 'AdminFeedbacks' }); break
        case 'Bookings Management': router.push({ name: 'BookingsManagement' }); break
        case 'Users Management': router.push({ name: 'UsersManagement' }); break
        case 'Incidents Management': router.push({ name: 'AdminIncidentsManagement' }); break
        default: break
      }
    }

    async function loadUserProfileDetails() {
      try {
        const apiProfiles = await getUserProfiles()
        let apiProfile = lodash.find(apiProfiles, { email: userProfile.value.email })
        if (apiProfile) {
          apiProfile = await getUserProfileById(apiProfile.id)
          userProfile.value.jobTitle = apiProfile.job || 'Employee'
          if (!apiProfile.profileImage) {
            const imageId = lodash.get(apiProfile, 'files.entities', []).find(file => file.type === 'profileImage')?.id
            if (imageId) {
              const profileImageFile = await getFile(imageId)
              const profileImage = profileImageFile ? await getDataFromBlob(profileImageFile) : user_avatar_3d
              userProfile.value.profileImage = profileImage
              apiProfile.profileImage = profileImage
              await storeBlob(`userProfile_${apiProfile.id}`, apiProfile)
            }
          } else {
            userProfile.value.profileImage = apiProfile.profileImage
          }
        }
      } catch (error) {
        console.error('Failed to load user profile details', error)
      } finally {
        if (!userProfile.value.profileImage) {
          userProfile.value.profileImage = user_avatar_3d
        }
        profileLoaded.value = true
      }
    }

    const appendFeatures = async () => {
      try {
        const features = await getFeatures()

        // Check if user is internal (has @pinestack email)
        const isInternalUser = userProfile.value?.email?.toLowerCase().includes('@pinestack')

        menuItems.value = menuItems.value.filter(item => {
          const title = item.title.toLowerCase()
          switch (title) {
            case t('menu.profile.title').toLowerCase():
              return features?.userOptions?.enabled && features?.userOptions?.profileUpdate
            case t('menu.room_control.title').toLowerCase():
              return features?.space?.enabled && features?.space?.roomHardwares
            case t('menu.elevator_control.title').toLowerCase():
              return features?.space?.enabled && features?.space?.elevators
            case t('menu.analytics.title').toLowerCase():
              return features?.analytics?.enabled
            case t('menu.occupancy.title').toLowerCase():
              return false
            case t('menu.incidents.title').toLowerCase():
              return features?.space?.enabled && features?.space?.inicidentCreation
            case t('menu.language.title').toLowerCase():
              return features?.appSettings?.enabled && features?.appSettings?.languageChange
            case t('menu.admin_panel.title').toLowerCase():
              return features?.admin?.enabled
            case t('menu.slido.title').toLowerCase():
              return features?.userOptions?.enabled && features?.userOptions?.slido
            case t('menu.commute.title').toLowerCase():
              return features?.commute?.enabled === true
            case t('menu.vouchers.title').toLowerCase():
              return features?.userOptions?.enabled && features?.userOptions?.vouchers
            case t('menu.feedback.title').toLowerCase():
              return features?.userOptions?.enabled && features?.userOptions?.feedback
            case t('menu.functions.title').toLowerCase():
              return features?.functionsList?.enabled
            case t('menu.signups.title').toLowerCase():
              return isInternalUser
            case t('menu.logout.title').toLowerCase():
              return true
            default:
              return false
          }
        })

        console.log('Menu items after filtering:', menuItems.value.length)
      } catch (error) {
        console.error('Failed to load features', error)
      }
    }

    onMounted(async () => {
      try {
        const [logoImage, userProfileData, webPlatform] = await Promise.all([
          getToolbarLogoImage(),
          OAuthService.getUserProfile(),
          isWebPlatform()
        ])

        // SET userProfile FIRST before calling appendFeatures
        userProfile.value = userProfileData

        isPia.value = await isPIA()
        isAdmin.value = userProfileData?.realm_access?.roles?.includes('globaladmin') || false

        console.log('User profile loaded:', userProfileData)
        console.log('Is Admin:', isAdmin.value)

        await loadLanguageFromCookie()

        // NOW call appendFeatures (userProfile is set now)
        await appendFeatures()

        logo.value = logoImage
        profileLoaded.value = false
        await loadUserProfileDetails()
      } catch (error) {
        console.error(error)
      }
    })

    return {
      menuItems,
      logo,
      userProfile,
      router,
      isAdmin,
      goToMenuItem,
      user_avatar_3d,
      dialog,
      roomControls,
      goToHardwareControl,
      profileLoaded,
      dialogType,
      goToAdminControl,
      adminControls,
      showLanguageDialog,
      selectedLanguage,
      setLanguage,
      confirmLanguageChange,
      cancelLanguageChange,
      openLanguageDialog,
      german_flag,
      usa_flag,
      isPia
    }
  },
})
</script>

<style scoped>
.premium-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 8px;
}

.disabled-menu-card {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
  filter: grayscale(40%) brightness(0.95);
  position: relative;
}

.disabled-menu-card:hover {
  transform: none !important;
}

.disabled-menu-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  pointer-events: none;
  z-index: 4;
}

.selected {
  border: 2px solid #007bff;
  box-shadow: 0px 6px 16px rgba(0, 123, 255, 0.2);
}

.tile-card {
  border-radius: 12px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  text-align: center;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 100px;
}

.tile-card:hover {
  transform: translateY(-5px);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2);
}

.tile-card v-icon {
  font-size: 32px;
}

.disabled-card {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.fab-title {
  font-size: 0.85rem;
  font-weight: bold;
  text-align: center;
  margin-top: 8px;
}

.fab-button {
  box-shadow: none;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  position: relative;
  left: 0;
  right: 0;
}

.profile-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  margin-top: 0.5rem;
}

.avatar {
  margin-bottom: 0.5rem;
}

.avatar-name {
  font-size: 1.25rem;
  font-weight: bold;
  text-align: center;
  margin: 0.5rem 0;
}

@media (max-width: 600px) {
  .tile-card {
    max-width: 80px;
    padding: 8px;
  }

  .avatar-container {
    position: relative;
    left: 0;
    right: 0;
  }

  .avatar-name {
    font-size: 1rem;
  }
}

.custom-toolbar {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  color: #333;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
}

@supports (-webkit-touch-callout: none) {
  .custom-toolbar {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
}

.custom-toolbar-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  color: #333;
  margin-left: 10px;
}

.toolbar-icon {
  height: 30px;
}

.menu-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 1rem;
  align-items: center;
}

.cards-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 16px;
}

.cards-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.menu-card-col {
  flex: 1 1 calc(50% - 8px);
  max-width: calc(50% - 8px);
  box-sizing: border-box;
}

@media (min-width: 600px) {
  .menu-card-col {
    flex: 1 1 calc(33.33% - 8px);
    max-width: calc(33.33% - 8px);
  }
}

@media (min-width: 960px) {
  .menu-card-col {
    flex: 1 1 calc(25% - 8px);
    max-width: calc(25% - 8px);
  }

  .cards-wrapper {
    margin-bottom: 15rem;
  }
}

.menu-card {
  height: 120px;
  border-radius: 16px;
  transition: transform 0.3s ease, filter 0.3s ease;
  cursor: pointer;
  text-align: left;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
}

.menu-card:hover:not(.disabled-menu-card) {
  transform: translateY(-5px);
}

.icon-wrapper {
  background-color: #f0f4f8;
  padding: 8px;
  border-radius: 12px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.menu-icon {
  font-size: 30px;
}

.card-title,
.card-subtitle {
  white-space: break-spaces;
}

@media (min-width: 1200px) {
  .menu-card-col {
    flex: 1 1 calc(20% - 16px);
    max-width: 250px;
  }

  .menu-card {
    height: 140px;
  }

  .card-title {
    margin-top: 0.5rem;
    font-size: 18px;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 13px;
    white-space: break-spaces;
  }
}

@media (max-width: 780px) {
  .menu-card {
    height: 105px;
  }

  .card-title {
    font-size: 18px;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 13px;
    white-space: break-spaces;
  }

  .menu-icon {
    font-size: 24px;
  }

  .icon-wrapper {
    padding: 6px;
  }

  .lock-overlay v-icon {
    font-size: 36px !important;
  }
}

@media (min-width: 1200px) and (max-width: 1300px) {
  .menu-card {
    height: 140px;
  }

  .card-title {
    font-size: 16px;
    font-weight: bold;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 14px;
    white-space: break-spaces;
  }
}

@media (min-width: 800px) and (max-width: 1200px) {
  .menu-card {
    height: 110px;
  }

  .card-title {
    font-size: 16px;
    font-weight: bold;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 13px;
    white-space: break-spaces;
  }

  .menu-icon {
    font-size: 20px;
  }

  .icon-wrapper {
    padding: 4px;
  }
}

@media (min-width: 480px) and (max-width: 800px) {
  .menu-card {
    height: 105px;
  }

  .card-title {
    font-size: 14px;
    font-weight: bold;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 13px;
    white-space: break-spaces;
  }

  .menu-icon {
    font-size: 20px;
  }

  .icon-wrapper {
    padding: 4px;
  }
}

@media (max-width: 480px) {
  .menu-card {
    height: 105px;
  }

  .card-title {
    font-size: 11px;
    font-weight: bold;
    white-space: break-spaces;
  }

  .card-subtitle {
    font-size: 11px;
    white-space: break-spaces;
  }

  .menu-icon {
    font-size: 20px;
  }

  .icon-wrapper {
    padding: 4px;
  }

  .premium-badge {
    font-size: 0.6rem;
    padding: 2px 6px;
  }

  .lock-overlay v-icon {
    font-size: 36px !important;
  }
}
</style>